// The frame every web app page sits in: a top bar (logo, nav, account) and,
// on phones, a bottom tab bar — Instagram's layout. The now-playing bar
// (player.tsx) docks above the tab bar on phones and at the very bottom on
// larger screens.

import type { ReactNode } from "react";
import { Link, NavLink } from "react-router";
import { BarChart3 as BarChart, Bell, Disc3, Library, LogOut, Search, UserRound } from "lucide-react";
import { Button } from "@web-shared/components/ui/button";
import { profilePath } from "@/lib/ctas";
import { cn } from "@web-shared/lib/utils";
import { useAuthGate } from "./auth";
import { useSession } from "./session";
import { Avatar } from "./components/Avatar";
import { BetaBadge } from "./BetaBadge";
import { useUnreadCount } from "./pages/NotificationsPage";
import { useRegisterBottomChrome } from "./chrome";

/** What the phone tab bar occupies, safe area included. */
const TABBAR_H = "calc(var(--tabbar-h) + var(--safe-b))";

export function Shell({
  beta,
  signOut,
  wide = false,
  children,
}: {
  beta: boolean;
  signOut: () => void;
  /** Seed pages and profiles use more of a big screen than the feed does. */
  wide?: boolean;
  children: ReactNode;
}) {
  const session = useSession();
  const gate = useAuthGate();
  const handle = session?.user.handle;
  const unread = useUnreadCount();
  /** The feed: / signed in; signed out, / is the first-visit questions, so
   * Discover goes to the feed's other address. */
  const feed = session ? "/" : "/discover";

  // The tab bar below is phone-only, but so is everything that docks above it
  // (player.tsx's mini bar, the seed page's Remix strip) — a desktop render
  // registers a height nothing reads, which is cheaper than teaching the
  // registry about breakpoints.
  useRegisterBottomChrome("tabbar", TABBAR_H);

  return (
    <div className="min-h-dvh pb-[calc(var(--tabbar-h)+var(--safe-b))] sm:pb-0">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center gap-1 px-6 py-3">
          <Link to="/" className="mr-4 flex shrink-0 items-center gap-1.5" aria-label="Seedbed home">
            <img src="/seedbed-logo.webp" alt="" className="size-7" />
            <span
              className="bg-gradient-to-br from-white to-zinc-400 bg-clip-text text-transparent"
              style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.3px" }}
            >
              Seedbed
            </span>
          </Link>
          {beta && <BetaBadge />}
          {/* The same four destinations as the phone's tab bar below, on
              every page — the home page included, where orientation matters
              most. */}
          <nav className="hidden items-center gap-1 sm:flex" aria-label="Main">
            <TopLink to={feed} end>Discover</TopLink>
            <TopLink to="/charts">Charts</TopLink>
            <TopLink to="/jam">Jam</TopLink>
            <TopLink to="/library">Library</TopLink>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <Button asChild size="icon" variant="ghost" className="hidden sm:inline-flex" title="Search">
              <Link to="/search" aria-label="Search">
                <Search />
              </Link>
            </Button>
            {session ? (
              <>
                <Button asChild size="icon" variant="ghost" className="relative" title="Notifications">
                  <Link to="/notifications" aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}>
                    <Bell />
                    <UnreadDot n={unread} />
                  </Link>
                </Button>
                <Link
                  to={handle ? profilePath(handle) : "/"}
                  className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-accent/50"
                  title="Your profile"
                >
                  <Avatar name={session.user.username} url={null} className="size-7" />
                  <span className="hidden max-w-32 truncate text-xs text-muted-foreground sm:inline">
                    {session.user.username}
                  </span>
                </Link>
                <Button size="icon" variant="ghost" onClick={signOut} title="Sign out" aria-label="Sign out">
                  <LogOut />
                </Button>
              </>
            ) : (
              <>
                {/* A 20px icon is a 20px target: the hit area is the button,
                    not the glyph, so it gets a thumb's worth of one. */}
                <button
                  type="button"
                  onClick={() => gate.openSignIn("signin")}
                  className="flex size-10 cursor-pointer items-center justify-center rounded-md"
                  title="Sign in"
                  aria-label="Sign in"
                >
                  <UserRound className="size-5 text-muted-foreground hover:text-foreground transition-colors" />
                </button>
                <button
                  type="button"
                  onClick={() => gate.openSignIn("signup")}
                  className="hidden cursor-pointer rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-glow transition-all hover:bg-white hover:shadow-glow-strong sm:block"
                >
                  Join
                </button>
              </>
            )}
          </div>
        </div>
      </header>


      <main className={cn("mx-auto space-y-3 px-4 py-4", wide ? "max-w-5xl" : "max-w-2xl")}>{children}</main>

      <nav
        // Height is `--tabbar-h` (an h-12 tab over 0.75rem) plus the inset,
        // exactly — that arithmetic is what TABBAR_H hands to everything that
        // docks above this bar, so the two must not drift.
        className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-white/5 bg-[#0c0c11]/95 pb-[calc(0.75rem+var(--safe-b))] backdrop-blur sm:hidden"
        aria-label="Tabs"
      >
        <Tab to={feed} end icon={<img src="/seedbed-logo.webp" alt="" className="size-5" />} label="Discover" />
        <Tab to="/charts" icon={<BarChart className="size-5" />} label="Charts" />
        <Tab to="/jam" icon={<Disc3 />} label="Jam" />
        <Tab to="/library" icon={<Library />} label="Library" />
      </nav>
    </div>
  );
}


function TopLink({ to, end, children }: { to: string; end?: boolean; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn("rounded-md px-2 py-1 text-sm transition-colors", isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground")
      }
    >
      {children}
    </NavLink>
  );
}

function Tab({ to, end, icon, label }: { to: string; end?: boolean; icon: ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex h-12 flex-1 flex-col items-center justify-center gap-0.5 text-[10px] [&_svg]:size-5",
          isActive ? "text-primary" : "text-muted-foreground",
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

function UnreadDot({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--removed)] px-1 text-[9px] font-semibold leading-none text-white">
      {n > 99 ? "99+" : n}
    </span>
  );
}
